import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import QuoteApprovalDataGrid from "./QuoteApprovalDataGrid";
import {
  FormPage,
  TabPanel,
  selectSessionConfig,
  selectUser,
  QuoteRejectionModal,
  DisplaySpinnerContext,
  handleApproveRejectAction,
  notifyToast,
  ToastType,
} from "./index";
import QuoteApprovalDetails from "./QuoteApprovalDetails";
import { useNavigate } from "react-router-dom";

const QuoteApproval = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(props.isCoveringManager === true ? "1" : "0");
  const [rejectHandler, setRejectHandler] = useState({ isReject: false, rejectionReason: "", rejectedQuoteRecord: {} });
  const [refreshGridHandler, setRefreshGridHandler] = useState({ isRequired: false, quoteId: "" });

  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);
  const user = useSelector(selectUser);

  const { setIsCoveringManager, pdfViewer, handleCancel } = props;

  const onChangeRejectionReason = (event) => {
    try {
      setRejectHandler((prevState) => ({
        ...prevState,
        rejectionReason: event.target.value,
      }));
    } catch (err) {
      alert(err);
    }
  };

  const handleRejectConfirm = async (event) => {
    try {
      if (!rejectHandler.rejectionReason && rejectHandler.isReject) {
        notifyToast("Rejection Reason Mandatory", ToastType.ERROR);
      } else {
        spinnerCtx.setLoadingIndicator(true);
        let res = await handleApproveRejectAction(
          user.userRecord.email,
          settingsConfig.businessCode,
          settingsConfig.countryCode,
          "reject",
          rejectHandler.rejectedQuoteRecord,
          rejectHandler.rejectionReason
        );
        if (res && res.status && res.status === 200) {
          notifyToast("Quote is rejected", ToastType.SUCCESS);
          handleModalClose();
          if (!pdfViewer) {
            setRefreshGridHandler((prevState) => ({
              ...prevState,
              isRequired: true,
              quoteId: rejectHandler.rejectedQuoteRecord.quoteId,
            }));
          } else {
            handleCancel(event);
          }
        } else {
          alert("Action request failed");
        }
      }
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      alert(err);
    }
  };

  const handleModalClose = () => {
    try {
      setRejectHandler((prevState) => ({
        ...prevState,
        isReject: false,
        rejectionReason: "",
      }));
    } catch (err) {
      alert(err);
    }
  };

  const handleTabChange = (newTab) => {
    try {
      setSelectedTab(newTab);
      if (pdfViewer === false && selectedTab === "0") {
        setIsCoveringManager(true);
      } else if (pdfViewer === true && selectedTab === "0") {
        navigate(
          {
            pathname: "/quotelist",
          },
          { state: { isCoveringManager: true } }
        );
      } else if (selectedTab === "1") {
        setIsCoveringManager(false);
      }
    } catch (error) {
      alert(error);
    }
  };

  const approveOnClick = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);

      let res = await handleApproveRejectAction(
        user.userRecord.email,
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "approve",
        param
      );

      if (res && res.status && res.status === 200) {
        notifyToast("Quote is approved", ToastType.SUCCESS);
        setRefreshGridHandler((prevState) => ({
          ...prevState,
          isRequired: true,
          quoteId: param?.quoteId,
        }));
      } else {
        notifyToast("Quote approve action failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  return (
    <FormPage isList={true} isListWithTab={true}>
      {rejectHandler.isReject && (
        <QuoteRejectionModal
          quoteRejection={{
            initialWidth: 480,
            modalTitle: t("quoteApproval.quoteRejection"),
            modalFirstActionDesc: t("generic.cancel"),
            modalSecondActionDesc: t("generic.confirm"),
            onModalSecondActionClick: handleRejectConfirm,
            handleModalClose: handleModalClose,
            openModal: rejectHandler.isReject,
            rejectionTextVal: rejectHandler.rejectionReason,
            rejectionTextLabel: t("quoteApproval.quoteRejectionLabel"),
            handleRejectionTextChange: onChangeRejectionReason,
          }}
        />
      )}
      <TabPanel
        tabs={[
          {
            icon: "",
            label: t("quoteApproval.approver"),
            tabPanelStyle: {
              paddingLeft: "0px",
              paddingRight: "0px",
              paddingBottom: "0px",
              paddingTop: "0px",
            },
            children: pdfViewer ? (
              <QuoteApprovalDetails
                user={user}
                settingsConfig={settingsConfig}
                spinnerCtx={spinnerCtx}
                quoteId={props.quoteId}
                quoteRecord={props.quoteRecord}
                isCoveringManager={props.isCoveringManager}
                handleCancel={handleCancel}
                rejectHandler={rejectHandler}
                setRejectHandler={setRejectHandler}
                approveOnClick={approveOnClick}
              />
            ) : (
              <QuoteApprovalDataGrid
                user={user}
                settingsConfig={settingsConfig}
                spinnerCtx={spinnerCtx}
                rejectHandler={rejectHandler}
                setRejectHandler={setRejectHandler}
                approveOnClick={approveOnClick}
                handleRedirectionQuoteDetails={
                  props.handleRedirectionQuoteDetails ? props.handleRedirectionQuoteDetails : () => { }
                }
                listWithTab={true}
                coveringManager={false}
                refreshGridHandler={refreshGridHandler}
                setRefreshGridHandler={setRefreshGridHandler}
              />
            ),
          },
          {
            icon: "",
            label: t("quoteApprovalReassignmentList.coveringManager"),
            tabPanelStyle: {
              paddingLeft: "0px",
              paddingRight: "0px",
              paddingBottom: "0px",
              paddingTop: "0px",
            },
            children: pdfViewer ? (
              <QuoteApprovalDetails
                user={user}
                settingsConfig={settingsConfig}
                spinnerCtx={spinnerCtx}
                quoteId={props.quoteId}
                quoteRecord={props.quoteRecord}
                isCoveringManager={props.isCoveringManager}
                handleCancel={handleCancel}
                rejectHandler={rejectHandler}
                setRejectHandler={setRejectHandler}
                approveOnClick={approveOnClick}
              />
            ) : (
              <QuoteApprovalDataGrid
                user={user}
                settingsConfig={settingsConfig}
                spinnerCtx={spinnerCtx}
                rejectHandler={rejectHandler}
                setRejectHandler={setRejectHandler}
                approveOnClick={approveOnClick}
                handleRedirectionQuoteDetails={
                  props.handleRedirectionQuoteDetails ? props.handleRedirectionQuoteDetails : () => { }
                }
                listWithTab={true}
                coveringManager={true}
                refreshGridHandler={refreshGridHandler}
                setRefreshGridHandler={setRefreshGridHandler}
              />
            ),
          },
        ]}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
      />
    </FormPage>
  );
};

export default QuoteApproval;
